import { useEffect, useMemo, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import { useNavigate } from 'react-router-dom';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
// import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import { timelineItemClasses } from '@mui/lab';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Button, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useQuery } from 'urql';

import { CookieConsent } from '../../../Components/Consent/CookieConsent';
import { Property } from '../../../Graphql/Generated/graphql';
import { GET_ALL_CITIES_AND_STATES, GET_ALL_PROPERTIES } from '../../../Graphql/queries';
import { HOME_META_DESCRIPTION, keywords } from '../../../Types/MetaData';
import DisplayListings from '../../Components/DisplayListings/DisplayListings';
import PlatformFAQ from '../../Components/PlatformFAQ/PlatformFAQ';
import './Home.css';

type StateAbbreviations = { [state: string]: string };

const stateAbbreviations: StateAbbreviations = {
  'BAJA CALIFORNIA SUR': 'B.C.S.',
  ALABAMA: 'AL',
  ALASKA: 'AK',
  ARIZONA: 'AZ',
  ARKANSAS: 'AR',
  CALIFORNIA: 'CA',
  COLORADO: 'CO',
  CONNECTICUT: 'CT',
  DELAWARE: 'DE',
  'DISTRICT OF COLUMBIA': 'DC',
  FLORIDA: 'FL',
  GEORGIA: 'GA',
  HAWAII: 'HI',
  IDAHO: 'ID',
  ILLINOIS: 'IL',
  INDIANA: 'IN',
  IOWA: 'IA',
  KANSAS: 'KS',
  KENTUCKY: 'KY',
  LOUISIANA: 'LA',
  MAINE: 'ME',
  MARYLAND: 'MD',
  MASSACHUSETTS: 'MA',
  MICHIGAN: 'MI',
  MINNESOTA: 'MN',
  MISSISSIPPI: 'MS',
  MISSOURI: 'MO',
  MONTANA: 'MT',
  NEBRASKA: 'NE',
  NEVADA: 'NV',
  'NEW HAMPSHIRE': 'NH',
  'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM',
  'NEW YORK': 'NY',
  'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND',
  OHIO: 'OH',
  OKLAHOMA: 'OK',
  OREGON: 'OR',
  PENNSYLVANIA: 'PA',
  'RHODE ISLAND': 'RI',
  'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD',
  TENNESSEE: 'TN',
  TEXAS: 'TX',
  UTAH: 'UT',
  VERMONT: 'VT',
  VIRGINIA: 'VA',
  WASHINGTON: 'WA',
  WASHINGTO: 'WA',
  'WEST VIRGINIA': 'WV',
  WISCONSIN: 'WI',
  WYOMING: 'WY',
};

const Home = () => {
  // const places = [
  //     "Atlanta, GA", "Arlington, VA", "Baltimore, MD", "Boston, MA", "Chicago, IL",
  //     "Los Angeles, CA", "New York City, NY", "Washington, DC", "Pheonix, AZ"
  // ]

  const [places, setPlaces] = useState<string[]>([]);
  const [pauseCities, setPauseCities] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [pauseProperties, setPauseProperties] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);

  const [citiesResult, citiesRefetch] = useQuery({
    query: GET_ALL_CITIES_AND_STATES,
    variables: {},
    pause: pauseCities,
  });

  useEffect(() => {
    if (!citiesResult.error && citiesResult.data) {
      const stringifiedPlaces: string[] = [];
      citiesResult.data.getAllCitiesAndStates.forEach((place: any) => {
        const cityState = `${place.city}, ${place.state}`;
        if (!stringifiedPlaces.includes(cityState)) {
          stringifiedPlaces.push(cityState);
        }
      });
      setPauseCities(true);
      setPlaces(stringifiedPlaces);
    }
  }, [citiesResult.error, citiesResult.data]);

  const getAllVariables: any = useMemo(() => {
    return {
      searchCity: '',
      searchStart: '',
      searchEnd: '',
      pagination: {
        paginate: true,
        page: 0,
        pageSize: 3,
      },
    };
  }, []);

  const [propertiesResult, propertiesRefetch] = useQuery({
    query: GET_ALL_PROPERTIES,
    variables: getAllVariables,
    pause: pauseProperties,
  });

  useEffect(() => {
    if (!propertiesResult.error && propertiesResult.data) {
      // console.log(propertiesResult.data.getAllProperties)
      setProperties(propertiesResult.data.getAllProperties);
      setPauseProperties(true);
    }
  }, [propertiesResult.data, propertiesResult.error]);

  const navigate = useNavigate();

  const goToLocation = (city: string) => {
    navigate('/listings', { state: city });
  };

  // Function to standardize states to their abbreviations
  const getAbbreviation = (state: string) => {
    if (state) {
      const stateUpper = state.toUpperCase();
      const abbreviation = stateAbbreviations[stateUpper] || state;
      return abbreviation;
    } else {
      return '';
    }
  };

  type UniquePlaces = { [key: string]: boolean };
  const uniquePlaces: UniquePlaces = {};

  const deduplicatedPlaces = places.filter((place) => {
    const [city, state] = place.split(', ').map((part) => part.trim());
    const stateAbbreviation = getAbbreviation(state);
    const key = city.toLowerCase() + stateAbbreviation;

    if (!uniquePlaces[key]) {
      uniquePlaces[key] = true;
      return true;
    }
    return false;
  });
  const sortedPlaces = [...deduplicatedPlaces].sort((a, b) => {
    const stateA = getAbbreviation(a.split(', ')[1]);
    const stateB = getAbbreviation(b.split(', ')[1]);
    return stateA.localeCompare(stateB);
  });

  const initialOptions = [...sortedPlaces, "Can't find what you're looking for?"];

  return (
    <div>
      {/* Do this on every page, 15 or so */}
      <HelmetProvider>
        <Helmet>
          <title>FLXlease | Flexible Living. Monthly Stays.</title>
          <meta name='description' content={HOME_META_DESCRIPTION} />
          <meta name='keywords' content={keywords} />
          <link rel='canonical' href={`https://flxlease.com`} />
        </Helmet>
      </HelmetProvider>
      <div className='welcome'>
        <div className='pictureText'>
          <h1 className='welcomeText'>
            Flexible Living.
            <div className='smlBreak'>
              <br />
            </div>{' '}
            Monthly Stays.
          </h1>
          <div className='smlWelcomeText'>
            Discover a new way to rent ready-to-live, month-to-month homes nationwide in
            minutes.
          </div>

          <div className='welcomeInputs'>
            <div className='inputArea'>
              {/* TODO: Regular Search */}

              <div className='location'>
                <Autocomplete
                  className='locationInputs'
                  options={initialOptions}
                  groupBy={(option) => {
                    if (option === "Can't find what you're looking for?") {
                      return 'Other'; // Group the "Can't find what you're looking for?" option under 'Other'
                    }
                    const [, state] = option.split(', ').map((part) => part.trim());
                    return getAbbreviation(state); // Group by state abbreviation
                  }}
                  getOptionLabel={(option) => {
                    if (option === "Can't find what you're looking for?") {
                      return option;
                    }
                    const [city, state] = option.split(', ').map((part) => part.trim());
                    return `${city}`;
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label='Location' variant='outlined' />
                  )}
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      setLocationName(newValue);
                    }
                  }}
                  value={null} // Set the initial value to null
                  isOptionEqualToValue={(option, value) => option === value}
                  filterOptions={(options, { inputValue }) => {
                    const filteredOptions = options.filter((option) => {
                      if (option === "Can't find what you're looking for?") {
                        return true;
                      }
                      const [city, state] = option.split(', ').map((part) => part.trim());
                      const stateAbbreviation = getAbbreviation(state);
                      const label = `${city}, ${stateAbbreviation}`;
                      return label.toLowerCase().includes(inputValue.toLowerCase());
                    });

                    return filteredOptions;
                  }}
                  renderGroup={(params) => (
                    <li {...params} style={{ margin: '18px 10px' }}>
                      <strong>{params.group}</strong>
                      <li style={{ listStyle: 'none', paddingTop: '5px' }}>
                        {params.children}
                      </li>
                    </li>
                  )}
                />
              </div>

              <div className='startDate'>
                <input className='Dates' type='date' />
              </div>

              <div className='endDate'>
                <input className='Dates' type='date' />
              </div>

              <div className='goBtn'>
                <Button className='go' onClick={() => goToLocation(locationName)}>
                  <ArrowForwardIcon fontSize='large' />{' '}
                </Button>
              </div>
            </div>

            <div className='inputArea2'>
              <div className='browseMobile' onClick={() => navigate('/listings')}>
                Browse Listings &nbsp; <SearchIcon />
              </div>
            </div>
          </div>

          <div className='signUpText' onClick={() => navigate('/sign-up')}>
            Sign up now for $50 towards next months rent!
          </div>
        </div>

        <div className='bigPictureContainer'>
          <img className='homePic' src='../../imgs/Home.webp' alt='' loading='eager' />
          {/* <video className="homePic" src={"../imgs/FLX_Background_VIDEO.m4v"} autoPlay muted loop></video> */}
        </div>
      </div>

      <div className='popularLocations'>
        <div className='locationHeader'>
          <div className='locationTitle'>Where to next?</div>
          <div className='smlLocationTitle'>
            <div className='explore' onClick={() => navigate('/listings')}>
              <div className='exploreText'>Explore our most desired locations</div> &nbsp;
              <ArrowForwardIosIcon style={{ fontSize: 'medium' }} />
            </div>
          </div>
        </div>

        {/* TODO: Predefined places (NY, Tampa, LA, etc...) */}

        <div className='locationBody'>
          <div className='lBContainer'>
            <div className='addGradient' onClick={() => goToLocation('Washington, DC')}>
              <div className='lbName'>Washington DC</div>
              <img className='lbImage' src='../../imgs/Maryland.webp' alt='' loading='lazy' />
            </div>
          </div>
          <div className='lBContainer'>
            <div className='addGradient' onClick={() => goToLocation('New York, NY')}>
              <div className='lbName'>New York City</div>
              <img className='lbImage' src='../../imgs/nyc.webp' alt='' loading='lazy' />
            </div>
          </div>
          <div className='lBContainer'>
            <div className='addGradient' onClick={() => goToLocation('Atlanta, GA')}>
              <div className='lbName'>Atlanta</div>
              <img className='lbImage' src='../../imgs/atlanta.webp' alt='' loading='lazy' />
            </div>
          </div>
          <div className='lBContainer'>
            <div className='addGradient' onClick={() => goToLocation('Boston, MA')}>
              <div className='lbName'>Boston</div>
              <img className='lbImage' src='../../imgs/boston.webp' alt='' loading='lazy' />
            </div>
          </div>
        </div>
      </div>

      <div className='whatIsFLX'>
        <div className='pageHeader'>ABOUT</div>
        <div className='aboutTitle'>
          What is <span className='flx'>FLX</span>lease?
        </div>

        <div className='aboutFlx'>
          <div className='aboutPicture'>
            <img className='aboutImg' src='../../imgs/Home.webp' alt='' loading='lazy' />
          </div>

          <div className='aboutText'>
            <div className='aboutTextContainer'>
              <p className='aboutP' style={{ fontSize: '1em' }}>
                We decided to start this journey after we saw our friends struggle to get out
                of fixed-term leases and those who were transitioning in life to find flexible
                living situations to suit their unique needs. Being stuck in a lease you no
                longer wish to continue is a pain, whether you&apos;re traveling for extended
                periods or wanting to change your current living situation.
              </p>
              <p className='aboutP' style={{ fontSize: '1em' }}>
                <b>That’s where we come in.</b>
              </p>
              <p className='aboutP' style={{ fontSize: '1em' }}>
                Our platform allows you to access leases that are favorable to both you and
                your landlord.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='whyFlx'>
        <div className='whyFlxTitle'>
          So, why <span className='flx'>FLX</span>lease?
        </div>

        <div className='whyContainer'>
          <div className='whyPoints'>
            <div className='whyIcon'>
              <img className='icon1' src='../../imgs/FLXman.png' alt='' loading='lazy' />
            </div>
            <div className='whyHeader'>Tailored Flexible Leases</div>
            <div className='whyText'>
              We cater to your lifestyle so you don’t need to be trapped in a long-term lease.
              You can also switch homes within our network with ease.
            </div>
          </div>

          <div className='whyPoints why2'>
            <div className='whyIcon'>
              <img className='icon2' src='../../imgs/furniture.png' alt='' loading='lazy' />
            </div>
            <div className='whyHeader'>Fully Furnished Living</div>
            <div className='whyText'>
              Feel like home anywhere you stay with hundreds of ready to move-in homes
              available for rent anytime. Just bring your suitcase.
            </div>
          </div>

          <div className='whyPoints why2'>
            <div className='whyIcon'>
              <img className='icon3' src='../../imgs/shield.png' alt='' loading='lazy' />
            </div>
            <div className='whyHeader'>Everything Online</div>
            <div className='whyText'>
              Our aim is to make your booking process feel frictionless and secure. View
              verified listings, virtual 360 tours, and fast bookings to make finding your next
              rental a breeze.
            </div>
          </div>
        </div>
      </div>

      <div style={{ opacity: '.5' }} className='pageBreak'></div>

      <div className='howitWorks'>
        <div className='howTitle'>How it works</div>

        <div className='howSection'>
          <div className='howPicture'>
            <img
              className='hPicture'
              src='../../imgs/HowPicture.webp'
              alt='how'
              loading='lazy'
            />
          </div>
          <div className='howTimeline'>
            <Timeline
              sx={{
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 1,
                },
              }}
            >
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot color='primary'>
                    <CheckIcon fontSize='small' />
                  </TimelineDot>
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ py: 'fit-content', px: 3 }}>
                  <Typography variant='h5' component='span'>
                    Search
                  </Typography>
                  <Typography className='howTimelineItems'>
                    Sort by your preferences, see 3D walkthroughs, and get your questions
                    answered. We confirm your identification before allowing you to lease.
                  </Typography>
                </TimelineContent>
              </TimelineItem>
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot color='primary'>
                    <CheckIcon fontSize='small' />
                  </TimelineDot>
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ py: 'fit-content', px: 3 }}>
                  <Typography variant='h5' component='span'>
                    Sign Lease
                  </Typography>
                  <Typography className='howTimelineItems'>
                    Review lease terms, set up payments, and move in. We handle any questions
                    or trouble calls with regards to maintenance, cleaning, services, and
                    utilities
                  </Typography>
                </TimelineContent>
              </TimelineItem>
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot color='primary'>
                    <CheckIcon fontSize='small' />
                  </TimelineDot>
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ py: 'fit-content', px: 3 }}>
                  <Typography variant='h5' component='span'>
                    FLXlease!
                  </Typography>
                  <Typography className='howTimelineItems'>
                    Renew, end early, or find your next lease.
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            </Timeline>
          </div>
        </div>
      </div>

      <div style={{ opacity: '.5' }} className='pageBreak'></div>

      <div className='recentListings'>
        <div className='locationHeader featuredHeader'>
          <div className='locationTitle'>Featured listings</div>
          <div className='smlLocationTitle'>
            <div className='explore' onClick={() => navigate('/listings')}>
              <div className='exploreText'>See all listings</div> &nbsp;
              <ArrowForwardIosIcon style={{ fontSize: 'medium' }} />
            </div>
          </div>
        </div>

        {/* TODO: Featured Listings (grab first 3 based on date) */}

        <DisplayListings properties={properties} />
      </div>

      <div style={{ opacity: '.5' }} className='pageBreak'></div>

      <div className='stayWithFlx'>
        <div className='stayText'>
          <div className='stayTitle'>
            Stay with <span className='flx'>FLX</span>lease
          </div>
          <div className='stayBody'>
            After hearing our friends struggle to find a replacement for their existing lease
            or attempting to exit a lease early, we decided there has to be a better way to
            lease than the status quo. So we figured, let&apos;s try and fix this old system.
            Our objective is simple. We want to build a leasing network that makes it easy for
            renters to access flexible leases and for landlords to be able to easily rent out
            and manage their property without losing money on vacancies or property management
            fees. We&apos;re building a safe environment to host this marketplace to keep out
            scammers and other bad actors, while creating win-win situations for everyone
            involved.
          </div>
        </div>

        <div className='stayVideo'>
          <div className='videoContainer'>
            <LazyLoadComponent>
              <embed
                width='100%'
                height='100%'
                src='https://player.vimeo.com/video/830382249?h=f38958ca77&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479'
                className='video'
              ></embed>
            </LazyLoadComponent>
          </div>
        </div>
      </div>

      <div style={{ opacity: '.5' }} className='pageBreak'></div>

      <div>
        <PlatformFAQ />
      </div>
      <CookieConsent />
    </div>
  );
};
export default Home;
